import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { 
  PhotoCamera as PhotoIcon, 
  Description as DescriptionIcon, 
  Mail as MailIcon, 
  ExpandMore as ExpandMoreIcon, 
  Folder as FolderIcon, 
  Block as BlockIcon,
  CameraAlt as CameraAltIcon,
  FormatAlignJustify as FormatAlignJustifyIcon,
  HomeWork as HomeWorkIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  GppMaybe as GppMaybeIcon,
  ReportProblem as ReportProblemIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import PhotoDoc from './appendixFiles/PhotoDoc';
import NonReleaseBody from './appendixFiles/NonReleaseBody';
import CensentSearch from './appendixFiles/CensentSearch';
import WrittenStatement from './appendixFiles/WrittenStatement';
import ZonalDocs from './ZonalDocs';
import Rights from './appendixFiles/Rights';
import GeneralAttestation from './appendixFiles/GeneralAttestation';
import AttorneyWaiver from './appendixFiles/AttorneyWaiver';

// Mapping des composants
const componentMap = {
  PhotoDoc: PhotoDoc,
  Rights: Rights,
  NonReleaseBody: NonReleaseBody,
  CensentSearch: CensentSearch,
  WrittenStatement: WrittenStatement,
  GeneralAttestation: GeneralAttestation,
  AttorneyWaiver: AttorneyWaiver
};

// Mapping des icônes
const iconMap = {
  CameraAlt: CameraAltIcon,
  FormatAlignJustify: FormatAlignJustifyIcon,
  Block: BlockIcon,
  Folder: FolderIcon,
  HomeWork: HomeWorkIcon,
  Description: DescriptionIcon,
  Gavel: GavelIcon,
  Assignment: AssignmentIcon,
  GppMaybe: GppMaybeIcon,
  ReportProblem: ReportProblemIcon,
  Edit: EditIcon
};

function Appendix() {
  const [appendixList, setAppendixList] = useState([]);
  const [selectedAppendix, setSelectedAppendix] = useState('');

  useEffect(() => {
    const fetchAppendixList = async () => {
      try {
        const response = await fetch('/api/appendices');
        const data = await response.json();
        
        // Liste des versions supportées (qui ont un composant disponible)
        const supportedVersions = Object.keys(componentMap);

        // Fallbacks locaux pour les annexes non présentes dans l'API
        const localFallbacks = [
          { version: 'PhotoDoc', title: 'Dossier photographique', icon: 'CameraAlt', show: true },
          { version: 'Rights', title: 'Droits Salduz', icon: 'GppMaybe', show: true },
          { version: 'NonReleaseBody', title: 'Avis de non disposition du corps', icon: 'ReportProblem', show: true },
          { version: 'CensentSearch', title: 'Visite domiciliaire', icon: 'HomeWork', show: true },
          { version: 'WrittenStatement', title: 'Déclaration écrite', icon: 'Edit', show: true },
          { version: 'GeneralAttestation', title: 'Attestation générale', icon: 'Assignment', show: true },
          { version: 'AttorneyWaiver', title: 'Renonciation à un avocat', icon: 'Gavel', show: true }
        ];

        // Fusionner les données de l'API avec les fallbacks
        // Pour chaque fallback, vérifier si l'annexe existe dans l'API
        const mergedAppendices = localFallbacks.map(fallback => {
          const apiData = data.find(item => item.version === fallback.version);
          // Si l'annexe existe dans l'API, utiliser ses données (notamment show)
          // Sinon, utiliser le fallback (show: true par défaut)
          return apiData || fallback;
        });

        // Filtrer pour n'afficher que les annexes visibles et supportées
        const visibleAppendices = mergedAppendices.filter(
          item => item.show === true && supportedVersions.includes(item.version)
        );

        setAppendixList(visibleAppendices);
        
        // Sélectionner le premier élément par défaut
        if (visibleAppendices.length > 0) {
          setSelectedAppendix(visibleAppendices[0].version);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des annexes:', error);
      }
    };

    fetchAppendixList();
  }, []);

  const getIconComponent = (iconName) => {
    const IconComponent = iconMap[iconName] || DescriptionIcon;
    return <IconComponent fontSize="small" sx={{ mr: 1 }} />;
  };

  const renderSelected = () => {
    // Si c'est "zonaldocs", afficher ZonalDocs
    if (selectedAppendix === 'zonaldocs') {
      return <ZonalDocs />;
    }
    
    // Sinon, chercher dans la liste et afficher le composant correspondant
    const selected = appendixList.find(item => item.version === selectedAppendix);
    if (selected) {
      const Component = componentMap[selected.version];
      return Component ? <Component /> : null;
    }
    
    return null;
  };

  const getCurrentLabel = () => {
    if (selectedAppendix === 'zonaldocs') {
      return { icon: <FolderIcon fontSize="small" sx={{ mr: 1 }} />, label: 'Documents zonaux' };
    }
    const selected = appendixList.find(item => item.version === selectedAppendix);
    if (selected) {
      return { icon: getIconComponent(selected.icon), label: selected.title };
    }
    return { icon: null, label: '' };
  };

  return (
    <Box sx={{ p: 2 }}>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Type d'annexe</InputLabel>
        <Select
          label="Type d'annexe"
          value={selectedAppendix}
          onChange={(e) => setSelectedAppendix(e.target.value)}
          IconComponent={ExpandMoreIcon}
          renderValue={(value) => {
            const current = getCurrentLabel();
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {current.icon}
                {current.label}
              </Box>
            );
          }}
          sx={{
            borderRadius: 1.5,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 1 },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
                borderRadius: 1.5,
                '& .MuiMenuItem-root': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }
              }
            }
          }}
        >
          {/* Afficher dynamiquement les annexes depuis l'API */}
          {appendixList.map((item) => (
            <MenuItem key={item.version} value={item.version}>
              {getIconComponent(item.icon)}
              {item.title}
            </MenuItem>
          ))}
          
          {/* Toujours afficher Documents zonaux à la fin */}
          <MenuItem value="zonaldocs">
            <FolderIcon fontSize="small" sx={{ mr: 1 }} />
            Documents téléchargeables
          </MenuItem>
        </Select>
      </FormControl>
      {renderSelected()}
    </Box>
  );
}

export default Appendix;



